
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

type ProfileData = {
  id: string;
  name: string;
  imageUrl: string;
  role: string;
  category: string;
  description: string;
  lastActive: string;
};

const profileData: ProfileData[] = [
  {
    id: "1",
    name: "John Doe",
    imageUrl: "https://source.unsplash.com/300x300/?face",
    role: "Caregiver",
    category: "professional",
    description: "Certified Nurse Assistant with 5 years of experience.",
    lastActive: "5 minutes ago",
  },
  {
    id: "2",
    name: "Jane Smith",
    imageUrl: "https://source.unsplash.com/301x301/?face",
    role: "Family",
    category: "family",
    description: "Daughter of Emily, manages schedules and appointments.",
    lastActive: "10 minutes ago",
  },
  {
    id: "3",
    name: "Robert Chen",
    imageUrl: "https://source.unsplash.com/302x302/?face",
    role: "Volunteer",
    category: "volunteer",
    description: "Friendly neighbor who helps with grocery shopping.",
    lastActive: "30 minutes ago",
  },
  {
    id: "4",
    name: "Aisha Khan",
    imageUrl: "https://source.unsplash.com/303x303/?face",
    role: "Family",
    category: "family",
    description: "Granddaughter of Emily, provides tech support.",
    lastActive: "1 hour ago",
  },
  {
    id: "5",
    name: "Kevin Lee",
    imageUrl: "https://source.unsplash.com/304x304/?face",
    role: "Professional",
    category: "professional",
    description: "Physical therapist specializing in geriatric care.",
    lastActive: "2 hours ago",
  },
];

const memberPositions = [
  { id: "1", x: "30%", y: "25%" },
  { id: "2", x: "70%", y: "25%" },
  { id: "3", x: "70%", y: "70%" },
  { id: "4", x: "30%", y: "70%" },
  { id: "5", x: "15%", y: "50%" },
];

const connectionVariants = {
  hidden: { opacity: 0, pathLength: 0 },
  visible: {
    opacity: 0.7,
    pathLength: 1,
    transition: {
      duration: 2,
      ease: "easeInOut",
    },
  },
};

const backgroundSpheres = [
  { size: 60, x: "15%", y: "15%", color: "#F2FCE2", delay: 0.2 },
  { size: 80, x: "85%", y: "20%", color: "#FEF7CD", delay: 0.5 },
  { size: 70, x: "75%", y: "85%", color: "#E5DEFF", delay: 0.8 },
  { size: 50, x: "10%", y: "80%", color: "#FFDEE2", delay: 1.1 },
  { size: 90, x: "50%", y: "90%", color: "#D3E4FD", delay: 1.4 },
  { size: 40, x: "90%", y: "50%", color: "#FDE1D3", delay: 1.7 },
];

export const BubbleCareAnimation = () => {
  const [activeMember, setActiveMember] = useState<ProfileData | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    profileData.forEach((profile) => {
      const img = new Image();
      img.src = profile.imageUrl;
    });
  }, []);

  const handleMemberClick = (member: ProfileData) => {
    setActiveMember(member);
  };

  const closePopover = () => {
    setActiveMember(null);
  };

  // Enhanced function to get category color with full opacity for badges and avatars
  const getCategoryColor = (category: string, isBackground = false) => {
    if (isBackground) {
      switch (category) {
        case "family": return "rgba(236, 72, 153, 0.7)"; // Pink with opacity
        case "professional": return "rgba(59, 130, 246, 0.7)"; // Blue with opacity
        case "volunteer": return "rgba(245, 158, 11, 0.7)"; // Amber with opacity
        default: return "rgba(156, 163, 175, 0.7)"; // Gray with opacity
      }
    } else {
      switch (category) {
        case "family": return "bg-pink-500"; // Solid pink
        case "professional": return "bg-blue-500"; // Solid blue
        case "volunteer": return "bg-amber-500"; // Solid amber
        default: return "bg-gray-500"; // Solid gray
      }
    }
  };

  // Get badge variant based on category
  const getBadgeVariant = (category: string): "destructive" | "default" | "secondary" | "outline" => {
    switch (category) {
      case "family": return "destructive";
      case "professional": return "default";
      case "volunteer": return "secondary";
      default: return "secondary";
    }
  };

  // Get shadow color for avatars
  const getShadowColor = (category: string) => {
    switch (category) {
      case "family": return "shadow-[0_0_15px_rgba(236,72,153,0.3)]";
      case "professional": return "shadow-[0_0_15px_rgba(59,130,246,0.3)]";
      case "volunteer": return "shadow-[0_0_15px_rgba(245,158,11,0.3)]";
      default: return "shadow-md";
    }
  };

  return (
    <div className="w-full h-full relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-tech-50/20 rounded-xl z-0"></div>
      
      {backgroundSpheres.map((sphere, index) => (
        <motion.div
          key={`bg-bubble-${index}`}
          className="absolute rounded-full z-0"
          style={{
            width: sphere.size,
            height: sphere.size,
            left: sphere.x,
            top: sphere.y,
            backgroundColor: sphere.color,
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.4 }}
          transition={{
            delay: sphere.delay,
            duration: 1,
            type: "spring",
            damping: 8,
          }}
        />
      ))}
      
      {/* Removed the duplicate legend that was shown on non-mobile screens */}
      
      <motion.div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                   bg-gradient-to-br from-bubble-500 to-bubble-400
                   rounded-full w-20 h-20 md:w-28 md:h-28
                   flex flex-col items-center justify-center text-white
                   shadow-[0_0_30px_rgba(147,51,234,0.3)] border-4 border-white z-30"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 10, stiffness: 100, delay: 0.5 }}
      >
        <span className="text-sm md:text-lg font-bold">Emily's</span>
        <span className="text-xs md:text-sm">Bubble</span>
      </motion.div>

      <svg className="absolute inset-0 w-full h-full pointer-events-none z-20">
        {memberPositions.map((pos, index) => {
          const profile = profileData[index];
          return (
            <motion.path
              key={`line-${pos.id}`}
              d={`M ${pos.x}, ${pos.y} Q ${parseFloat(pos.x) < 50 ? 
                parseFloat(pos.x) + 15 : parseFloat(pos.x) - 15}%, ${parseFloat(pos.y) < 50 ? 
                parseFloat(pos.y) + 15 : parseFloat(pos.y) - 15}% 50%, 50%`}
              strokeWidth="2"
              stroke={getCategoryColor(profile.category, true)}
              fill="transparent"
              variants={connectionVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.8 + index * 0.2 }}
              filter="drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1))"
            />
          );
        })}
      </svg>

      {memberPositions.map((pos, index) => {
        const profile = profileData[index];
        
        return (
          <Popover key={pos.id} open={activeMember?.id === profile.id} onOpenChange={(open) => !open && closePopover()}>
            <PopoverTrigger asChild>
              <motion.div
                className="absolute flex flex-col items-center cursor-pointer z-30"
                style={{ left: pos.x, top: pos.y }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1 + index * 0.2, duration: 0.4 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleMemberClick(profile)}
              >
                <Avatar className={`w-10 h-10 md:w-12 md:h-12 ${getCategoryColor(profile.category)} ${getShadowColor(profile.category)} border-2 border-white`}>
                  <AvatarImage src={profile.imageUrl} alt={profile.name} />
                  <AvatarFallback className={getCategoryColor(profile.category)}>
                    {profile.name.split(" ").map((n) => n[0]).join("")}
                  </AvatarFallback>
                </Avatar>
                <span className="mt-1 text-xs md:text-sm font-medium text-gray-700 drop-shadow-sm">{profile.name}</span>
                <div className="mt-1">
                  <Badge variant={getBadgeVariant(profile.category)} className="text-[10px] md:text-xs px-2 py-0 h-auto shadow-sm">
                    {profile.role}
                  </Badge>
                </div>
              </motion.div>
            </PopoverTrigger>
            <PopoverContent className="w-64 md:w-80 bg-white border border-gray-200 rounded-lg shadow-md p-4 z-50">
              <div className="flex items-center space-x-4 mb-3">
                <Avatar className={`w-12 h-12 md:w-14 md:h-14 ${getCategoryColor(profile.category)}`}>
                  <AvatarImage src={profile.imageUrl} alt={profile.name} />
                  <AvatarFallback className={getCategoryColor(profile.category)}>
                    {profile.name.split(" ").map((n) => n[0]).join("")}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="text-base md:text-lg font-semibold">{profile.name}</h4>
                  <p className="text-xs md:text-sm text-gray-500">{profile.role}</p>
                </div>
              </div>
              <p className="text-xs md:text-sm text-gray-700 mb-2">{profile.description}</p>
              <div className="flex items-center space-x-2 text-gray-500 text-[10px] md:text-xs">
                <span>Last active:</span>
                <span>{profile.lastActive}</span>
              </div>
            </PopoverContent>
          </Popover>
        );
      })}
    </div>
  );
};
